import { HttpErrorResponse } from '@angular/common/http';
import { extractHttpErrorMessage } from '../../shared/utils/http-error.util';
import { Injectable, inject, signal } from '@angular/core';
import { NetworkConnectivityService } from '../../shared/services/network-connectivity.service';
import { ToastService } from '../../shared/services/toast.service';
import { UserService } from '../../shared/services/user.service';
import { AgenticAiApiService } from './agentic-ai-api.service';
import type {
  AgenticAiConversation,
  AgenticAiConversationListResponse,
  AgenticAiMessage,
  PendingThreadBootstrap,
} from './agentic-ai.models';
import { skillSlashTokenFromMetadata } from '../skills/skill-slash.util';

/** Persists BFF UI across `/ai-agent` → `/ai-agent/:id` (new `AiAgent` instance). */
export interface BffRouteHandoffSnapshot {
  conversationId: string;
  messages: AgenticAiMessage[];
  assistantMessage: string | null;
  bffClarifyOptions: string[];
  bffSuggestions: string[];
  bffDisplayedUserPrompt: string;
  searchstart: boolean;
  /**
   * After BFF `onResult`, `/ai-agent` → `/ai-agent/:id` loads a new `AiAgent` instance.
   * Opening the BQ split panel must run there (not in a `setTimeout` on the destroyed instance).
   */
  autoOpenBqPanel?: boolean;
  /** Same handoff pattern for BFF `resultKind: email` — open sendmail compose from last assistant metadata. */
  autoOpenEmailCompose?: boolean;
  /** Same handoff pattern for BFF `resultKind: notes` — open notes panel from last assistant metadata. */
  autoOpenNotesPanel?: boolean;
  /** Same handoff pattern for BFF `resultKind: task_manager` — open task manager from last assistant metadata. */
  autoOpenTaskManagerPanel?: boolean;
  /** Same handoff pattern for BFF `resultKind: voice_ai` — open voice AI interview from last assistant metadata. */
  autoOpenVoiceAiPanel?: boolean;
  /** Direct `*_choice_needed` — open submission / interview choice panel after `/ai-agent/:id` remount. */
  autoOpenChoicePanel?: boolean;
  /** Refocus prompt after first message when `/ai-agent` navigates to `/ai-agent/:id`. */
  refocusPromptAfterFormSubmit?: boolean;
}

/** Shared UI state for agent chat (sidebar list + active id). Server remains source of truth. */
@Injectable({ providedIn: 'root' })
export class AgentChatStateService {
  private readonly api = inject(AgenticAiApiService);
  private readonly user = inject(UserService);
  private readonly network = inject(NetworkConnectivityService);
  private readonly toast = inject(ToastService);

  /** Initial / refresh page size for Recent chats (also used for `limit` query). */
  readonly recentChatsPageSize = 10;
  /** Cumulative count of rows returned by the server list API (for `offset`). */
  private listServerOffset = 0;

  private pendingThreadBootstrap: PendingThreadBootstrap | null = null;
  private bffRouteHandoff: BffRouteHandoffSnapshot | null = null;
  /** Session tombstones — prevents refresh from re-adding soft-deleted rows via orphan merge. */
  private readonly deletedConversationIds = new Set<string>();
  /**
   * Survives `/ai-agent/:id` → `/ai-agent` (new `AiAgent` instance). One-shot auto-submit for
   * Prompt Gallery / home prompt cards.
   */
  private pendingAutoSubmitPrompt: string | null = null;

  readonly conversations = signal<AgenticAiConversation[]>([]);
  readonly activeConversationId = signal<string | null>(null);

  /**
   * Optional handoff id when the UI must continue a thread without `:conversationId` in the URL.
   * Cleared on successful BFF result, new chat, or navigating away from the agent shell context.
   */
  readonly pendingContinueConversationId = signal<string | null>(null);
  readonly conversationsLoading = signal(false);
  readonly conversationsLoadingMore = signal(false);
  readonly conversationsHasMore = signal(false);
  readonly conversationsError = signal<string | null>(null);

  /**
   * Survives route `/ai-agent` → `/ai-agent/:id` (new `AiAgent` instance). One-shot: next param load
   * for this id skips Domain Orch GETs after BFF `onResult`.
   */
  readonly postBffSkipLoadConversationId = signal<string | null>(null);
  /**
   * Conversation id for BFF clarify continuation UI (`showBffMainPanel` vs thread).
   * Set after BFF `result` and when loading a thread (so state matches the open chat).
   */
  readonly bffStreamConversationId = signal<string | null>(null);
  /** Incremented when the agent prompt should be focused (e.g. New chat). */
  readonly promptFocusRequest = signal(0);

  requestPromptFocus(): void {
    this.promptFocusRequest.update((n) => n + 1);
  }

  setPostBffSkipLoadAfterResult(conversationId: string): void {
    this.postBffSkipLoadConversationId.set(conversationId);
  }

  /** Returns true if skip matched and was cleared (do not call `loadConversationThread`). */
  consumePostBffSkipLoadIfMatches(conversationId: string): boolean {
    const skip = this.postBffSkipLoadConversationId();
    if (skip !== null && skip === conversationId) {
      this.postBffSkipLoadConversationId.set(null);
      return true;
    }
    return false;
  }

  setBffStreamConversationId(id: string | null): void {
    this.bffStreamConversationId.set(id);
  }

  clearPostBffAndBffStreamFlags(): void {
    this.postBffSkipLoadConversationId.set(null);
    this.bffStreamConversationId.set(null);
    this.bffRouteHandoff = null;
  }

  setBffRouteHandoff(snapshot: BffRouteHandoffSnapshot): void {
    this.bffRouteHandoff = snapshot;
  }

  consumeBffRouteHandoffIfMatches(conversationId: string): BffRouteHandoffSnapshot | null {
    const h = this.bffRouteHandoff;
    if (h && h.conversationId === conversationId) {
      this.bffRouteHandoff = null;
      return h;
    }
    return null;
  }
  /** First user prompt / conversation title — shown in `app-ai-header` (left). */
  readonly conversationHeaderTitle = signal<string | null>(null);

  setConversationHeaderTitle(title: string | null): void {
    this.conversationHeaderTitle.set(title);
  }

  setPendingThreadBootstrap(bootstrap: PendingThreadBootstrap | null): void {
    this.pendingThreadBootstrap = bootstrap;
  }

  clearPendingThreadBootstrap(): void {
    this.pendingThreadBootstrap = null;
  }

  setPendingAutoSubmitPrompt(text: string | null): void {
    const t = text?.trim();
    this.pendingAutoSubmitPrompt = t ? t : null;
  }

  /** Returns and clears the pending prompt (one-shot). */
  consumePendingAutoSubmitPrompt(): string | null {
    const t = this.pendingAutoSubmitPrompt;
    this.pendingAutoSubmitPrompt = null;
    return t;
  }

  /** Returns and clears when `conversationId` matches (one-shot after BFF → navigate). */
  consumePendingThreadBootstrap(conversationId: string): PendingThreadBootstrap | null {
    const p = this.pendingThreadBootstrap;
    if (p && p.conversationId === conversationId) {
      this.pendingThreadBootstrap = null;
      return p;
    }
    return null;
  }

  /** New or updated chat row at the top of Recent chats (before list refresh). */
  prependOrUpsertConversation(c: AgenticAiConversation): void {
    this.deletedConversationIds.delete(c.id);
    const prev = this.conversations().find((x) => x.id === c.id);
    const merged = this.mergeConversationSkillMetadata(prev, c);
    const rest = this.conversations().filter((x) => x.id !== c.id);
    this.conversations.set(this.sortConversationsNewestFirst([merged, ...rest]));
  }

  /**
   * Marks a Recent-chats row as skill-started (lightbulb icon) and persists `metadata.skillSlash`.
   * Idempotent when the slash is already stored on the local list row.
   */
  markConversationAsSkill(conversationId: string, skillSlash: string): void {
    const id = conversationId.trim();
    const slash = skillSlashTokenFromMetadata({ skillSlash });
    if (!id || !slash) {
      return;
    }
    const existing = this.conversations().find((c) => c.id === id);
    const already = skillSlashTokenFromMetadata(existing?.metadata);
    if (already === slash) {
      return;
    }
    if (existing) {
      this.prependOrUpsertConversation({
        ...existing,
        metadata: {
          ...(existing.metadata ?? {}),
          skillSlash: slash,
        },
      });
    }
    if (!this.user.isAuthenticatedMethod() || !this.network.isOnline()) {
      return;
    }
    this.api.patchConversation(id, { metadata: { skillSlash: slash } }).subscribe({
      next: (updated) => {
        this.prependOrUpsertConversation(updated);
      },
      error: () => {
        /* Optimistic list row already updated when present; list refresh may catch up later. */
      },
    });
  }

  /** Optimistic remove after delete; blocks orphan re-merge on {@link refreshConversations}. */
  removeConversationFromList(conversationId: string): void {
    const id = conversationId.trim();
    if (!id) {
      return;
    }
    this.deletedConversationIds.add(id);
    this.conversations.update((prev) => prev.filter((c) => c.id !== id));
    if (this.activeConversationId() === id) {
      this.activeConversationId.set(null);
    }
  }

  setActiveConversationId(id: string | null): void {
    this.activeConversationId.set(id);
  }

  setPendingContinueConversationId(id: string | null): void {
    this.pendingContinueConversationId.set(id);
  }

  refreshConversations(): void {
    if (!this.user.isAuthenticatedMethod()) {
      this.conversations.set([]);
      this.conversationsLoading.set(false);
      this.conversationsLoadingMore.set(false);
      this.conversationsHasMore.set(false);
      this.listServerOffset = 0;
      this.conversationsError.set(null);
      return;
    }
    if (!this.network.isOnline()) {
      this.conversationsLoading.set(false);
      this.conversationsLoadingMore.set(false);
      this.conversationsError.set(null);
      return;
    }
    this.listServerOffset = 0;
    this.conversationsLoading.set(true);
    this.conversationsError.set(null);
    this.api
      .listConversations({ limit: this.recentChatsPageSize, offset: 0 })
      .subscribe({
        next: (res) => {
          const server = this.sortConversationsNewestFirst(res.items ?? []);
          const serverIds = new Set(server.map((c) => c.id));
          const prev = this.conversations();
          // Keep optimistic rows only when not deleted and not yet in the server page (new chat race).
          const orphans = prev.filter(
            (c) => !serverIds.has(c.id) && !this.deletedConversationIds.has(c.id),
          );
          this.conversations.set(this.mergeConversationsDedupSorted(orphans, server));
          for (const id of this.deletedConversationIds) {
            if (!serverIds.has(id)) {
              continue;
            }
            this.deletedConversationIds.delete(id);
          }
          this.applyListPageMeta(res, server, 'replace');
          this.conversationsLoading.set(false);
        },
        error: (err: unknown) => {
          this.conversationsLoading.set(false);
          this.handleConversationsError(err);
        },
      });
  }

  /**
   * Session-safe error handling for the conversations list:
   * - 401 → handled by `unauthorizedInterceptor` (toast + ATS redirect); skip duplicate UI.
   * - Otherwise → toast a friendly message. Never exposes the endpoint URL (see
   *   {@link extractHttpErrorMessage}), which previously leaked into the list div on status 0.
   */
  private handleConversationsError(err: unknown): void {
    if (err instanceof HttpErrorResponse && err.status === 401) {
      this.conversationsError.set(null);
      return;
    }
    const msg = extractHttpErrorMessage(err, 'Failed to load conversations');
    this.conversationsError.set(null);
    this.toast.error(msg || 'Failed to load conversations');
  }

  /** Next page for Recent chats (same `limit` / `offset` contract as {@link refreshConversations}). */
  loadMoreConversations(): void {
    if (
      !this.user.isAuthenticatedMethod() ||
      !this.network.isOnline() ||
      !this.conversationsHasMore() ||
      this.conversationsLoadingMore() ||
      this.conversationsLoading()
    ) {
      return;
    }
    this.conversationsLoadingMore.set(true);
    this.conversationsError.set(null);
    this.api
      .listConversations({
        limit: this.recentChatsPageSize,
        offset: this.listServerOffset,
      })
      .subscribe({
        next: (res) => {
          const batch = this.sortConversationsNewestFirst(res.items ?? []);
          this.conversations.update((prev) =>
            this.mergeConversationsDedupSorted(prev, batch),
          );
          this.applyListPageMeta(res, batch, 'append');
          this.conversationsLoadingMore.set(false);
        },
        error: (err: unknown) => {
          this.conversationsLoadingMore.set(false);
          this.handleConversationsError(err);
        },
      });
  }

  private mergeConversationsDedupSorted(
    a: AgenticAiConversation[],
    b: AgenticAiConversation[],
  ): AgenticAiConversation[] {
    const byId = new Map<string, AgenticAiConversation>();
    for (const c of [...a, ...b]) {
      const prev = byId.get(c.id);
      byId.set(c.id, prev ? this.mergeConversationSkillMetadata(prev, c) : c);
    }
    return this.sortConversationsNewestFirst([...byId.values()]);
  }

  /** Keep `metadata.skillSlash` from either side when merging list rows. */
  private mergeConversationSkillMetadata(
    prev: AgenticAiConversation | undefined,
    next: AgenticAiConversation,
  ): AgenticAiConversation {
    if (!prev) {
      return next;
    }
    const slash =
      skillSlashTokenFromMetadata(next.metadata) || skillSlashTokenFromMetadata(prev.metadata);
    if (!slash) {
      return { ...prev, ...next, metadata: next.metadata ?? prev.metadata ?? null };
    }
    return {
      ...prev,
      ...next,
      metadata: {
        ...(prev.metadata ?? {}),
        ...(next.metadata ?? {}),
        skillSlash: slash,
      },
    };
  }

  private applyListPageMeta(
    res: AgenticAiConversationListResponse,
    batch: AgenticAiConversation[],
    mode: 'replace' | 'append',
  ): void {
    if (mode === 'replace') {
      this.listServerOffset = batch.length;
    } else {
      this.listServerOffset += batch.length;
    }
    if (res.hasMore === true) {
      this.conversationsHasMore.set(true);
      return;
    }
    if (res.hasMore === false) {
      this.conversationsHasMore.set(false);
      return;
    }
    if (typeof res.total === 'number') {
      this.conversationsHasMore.set(this.listServerOffset < res.total);
      return;
    }
    this.conversationsHasMore.set(batch.length >= this.recentChatsPageSize);
  }

  private sortConversationsNewestFirst(items: AgenticAiConversation[]): AgenticAiConversation[] {
    return [...items].sort((a, b) => {
      const pa = Number(a.pinned ?? 0) === 1 ? 1 : 0;
      const pb = Number(b.pinned ?? 0) === 1 ? 1 : 0;
      if (pa !== pb) {
        return pb - pa; // pinned first
      }
      const ta = Date.parse(a.updatedAt ?? a.createdAt ?? '') || 0;
      const tb = Date.parse(b.updatedAt ?? b.createdAt ?? '') || 0;
      return tb - ta;
    });
  }

  private formatError(err: unknown): string {
    if (this.network.isOfflineHttpError(err) || !this.network.isOnline()) {
      return '';
    }
    return extractHttpErrorMessage(err, 'Failed to load conversations');
  }
}
